import * as React from 'react';
import { toTitleCase } from '@/utils/to-title-case';
import { 
  Avatar, 
  Box, 
  Chip, 
  Skeleton, 
  Stack, 
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link
} from '@mui/material';
import { PaperPlaneTilt as PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/ssr/PaperPlaneTilt';
import { CaretDown as CaretDownIcon } from '@phosphor-icons/react';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { Request_Status, type Request } from '@/types/v2/request';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { useUser } from '@/hooks/v2/use-user';
import { paths } from '@/paths';
import axios from '@/lib/v2/axios';

interface RequestHeaderProps {
  request?: Request | null;
  isLoading: boolean;
  error: any;
}

interface RequestStatus {
  id: number;
  name: string;
  color: string;
  description: string;
}

export function RequestHeader({ request, isLoading, error }: RequestHeaderProps): React.JSX.Element {
  const { user } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusMenuAnchor, setStatusMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [selectedStatus, setSelectedStatus] = React.useState<number | ''>('');

  // Check if user has permission to edit status
  const canEditStatus = React.useMemo(() => {
    if (!user) return false;

    const hasAccountRole = user.account_roles?.some(role =>
      role.name === ACCOUNT_ROLES.ACCOUNT_ADMIN ||
      role.name === ACCOUNT_ROLES.ACCOUNT_MANAGER ||
      role.name === ACCOUNT_ROLES.ACCOUNT_OWNER
    );

    const hasCommunityRole = user.community_roles?.some(role =>
      role.name === COMMUNITY_ROLES.COMMUNITY_MANAGER
    );

    return hasAccountRole || hasCommunityRole;
  }, [user]);

  // Fetch available statuses
  const { data: statuses } = useQuery<RequestStatus[]>({
    queryKey: ['request-statuses'],
    queryFn: async () => {
      const response = await axios.get('/api/v1/requests/statuses');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (statusId: number) => {
      const response = await axios.put(`/api/v2/communities/${request?.community_id}/requests/${request?.id}`, {
        status_id: statusId,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Request status updated successfully');
      queryClient.invalidateQueries({ queryKey: ['request', request?.id] });
      setStatusMenuAnchor(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update request status');
    },
  });

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>) => {
    if (canEditStatus) {
      setStatusMenuAnchor(event.currentTarget);
    }
  };

  const handleStatusSelect = (statusId: number) => {
    setSelectedStatus(statusId);
    updateStatusMutation.mutate(statusId);
  };

  const handleCloseMenu = () => {
    setStatusMenuAnchor(null);
  };

  const getStatusColor = (statusId: number) => {
    // First try to use the request_status relationship data
    if (request?.request_status?.color) {
      return request.request_status.color;
    }
    
    // Then try to find in the fetched statuses
    const status = statuses?.find(s => s.id === statusId);
    if (status?.color) {
      return status.color;
    }
    
    // Fallback to default color
    return '#666666';
  };

  const getStatusName = (statusId: number) => {
    // First try to use the request_status relationship data
    if (request?.request_status?.name) {
      return request.request_status.name;
    }
    
    // Then try to find in the fetched statuses
    const status = statuses?.find(s => s.id === statusId);
    if (status?.name) {
      return status.name;
    }
    
    // Fallback to enum values - only use string values, not number keys
    const statusName = Request_Status[statusId as keyof typeof Request_Status];
    return typeof statusName === 'string' ? statusName : 'Unknown';
  };

  return (
    <Stack spacing={4}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<ChevronRightIcon fontSize="small" />}
        sx={{ 
          mb: 2,
          '& .MuiBreadcrumbs-separator': {
            color: 'text.secondary',
          }
        }}
      >
        <Link
          component="button"
          onClick={() => { navigate(paths.requests.index); }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            textDecoration: 'none',
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
            }
          }}
        >
          Requests
        </Link>
        <Typography color="text.primary" sx={{ fontWeight: 500 }}>
          {request?.summary ? 
            (request.summary.length > 40 ? `${request.summary.slice(0, 40)}...` : request.summary) 
            : 'Request Details'
          }
        </Typography>
      </Breadcrumbs>

      {isLoading ? (
        <Stack direction="row" spacing={3} sx={{ alignItems: 'center' }} width="500px">
          <Skeleton height={64} variant="rectangular" width={64} />
          <Stack spacing={1}>
            <Skeleton height={30} variant="text" width={325} />
            <Skeleton height={25} variant="text" width={250} />
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={3} sx={{ alignItems: 'flex-start' }}>
          <Stack direction="row" position="relative" spacing={2} sx={{ alignItems: 'center', flex: '1 1 auto' }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <Box>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                  }}
                >
                  <PaperPlaneTiltIcon fontSize="var(--icon-fontSize-lg)" />
                </Avatar>
              </Box>
            </Stack>
            <div>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="h4">{request?.summary}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip label={toTitleCase(request?.request_type?.name || '')} size="small" />
                <Tooltip title={canEditStatus ? "Click to change status" : "Current status"}>
                  <Chip 
                    disabled={updateStatusMutation.isPending} 
                    icon={canEditStatus ? <CaretDownIcon size={16} /> : undefined}
                    label={toTitleCase(getStatusName(request?.status_id && typeof request.status_id === 'number' ? request.status_id : 0))}
                    onClick={handleStatusClick}
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor(request?.status_id && typeof request.status_id === 'number' ? request.status_id : 0),
                      color: 'white',
                      cursor: canEditStatus ? 'pointer' : 'default',
                      '&:hover': canEditStatus ? {
                        backgroundColor: getStatusColor(request?.status_id && typeof request.status_id === 'number' ? request.status_id : 0),
                        opacity: 0.8,
                      } : {},
                    }}
                  />
                </Tooltip>
              </Stack>
            </div>
          </Stack>
        </Stack>
      )}

      {/* Status Selection Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={handleCloseMenu}
        open={Boolean(statusMenuAnchor)}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {statuses?.map((status) => (
          <MenuItem
            disabled={updateStatusMutation.isPending}
            key={status.id}
            onClick={() => { handleStatusSelect(status.id); }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 200,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: status.color,
              }}
            />
            <Typography variant="body2">{toTitleCase(status.name)}</Typography>
            {status.id === request?.status_id && (
              <Typography color="text.secondary" sx={{ ml: 'auto' }} variant="caption">
                Current
              </Typography>
            )}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
}
